import { Tables } from "@/types/database.types";
import { ConversationStatus } from "@/types/conversation";

// Extended conversation type with all analytics fields
export type Conversation = Partial<Tables<"conversations">> & {
  agent_name?: string;
  avatar_url?: string | null;
  user_name?: string;
  user_email?: string | null;
  user_phone?: string | null;
  last_message?: string;
  last_message_at?: string;
  message_count?: number;
  form_field_values?: Record<string, string>;
  form_fields?: Record<string, string>;
  is_anonymous?: boolean;
  title?: string; // Agent title
  first_user_message?: string; // First message from the user
  // Analytics fields from the database
  priority?: "low" | "medium" | "high" | "urgent" | null;
  next_step?: string | null;
  intent?: string | null;
  summary?: string | null;
  tags?: string[] | null;
  sentiment?: "positive" | "neutral" | "negative" | "unknown" | null;
  user_type?: "lead" | "client" | "unknown" | null;
  estimated_time_saved?: number | null;
  status?: ConversationStatus | null; // Add status field
  // Extraction status fields
  extracted_fields_status?: "success" | "fail" | null;
  extracted_information_status?: "success" | "fail" | null;
};

// Define message type to match exactly what we need
export interface Message {
  id: string;
  created_at: string;
  sender_type: "user" | "ai";
  content: string;
}

// Database message structure (may include more fields than we need)
export interface DatabaseMessage {
  id: string;
  created_at: string;
  content: string;
  role?: string; // This might be in the API response
  sender_type?: "user" | "ai" | "agent_chat_user";
  conversation_id?: string;
  [key: string]: unknown; // For any other fields
}

export interface FormField {
  field_id: string;
  title: string;
  value: string;
  field_type?: string;
}

// Types for API responses
export interface ConversationDetailsResponse {
  conversation: Partial<Tables<"conversations">>;
  messages: DatabaseMessage[];
  formFields: FormField[];
  agentInfo?: {
    agent_name?: string;
    avatar_url?: string;
    title?: string;
  };
  userInfo?: {
    name?: string;
    email?: string;
    phone?: string;
  };
}

// Define a set of vibrant, modern colors for UI elements
export const colors = {
  agentBubble: "bg-blue-50 border-blue-200",
  agentText: "text-blue-800",
  agentIcon: "bg-blue-100 text-blue-600",
  userBubble: "bg-indigo-600",
  userText: "text-white",
  userIcon: "bg-indigo-700 text-white",
  formFieldBg: "bg-violet-50",
  formFieldIcon: "bg-violet-100 text-violet-600",
  formFieldCard: "bg-white shadow-sm",
  summaryBg: "bg-emerald-50",
  summaryIcon: "bg-emerald-100 text-emerald-600",
  selectedConversation: "border-l-2 border-blue-500 bg-blue-50",
  conversationHover: "hover:bg-slate-50",
  messageCount: "bg-indigo-100 text-indigo-700",
} as const;
